import React, { useState, useEffect } from "react";
import '../../styles/ClientDebtSummary.css'; // Optional, for styling
import Swal from 'sweetalert2';
import { Tabs, Tab, Box, Typography } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { formatCurrencyUSD } from "../../utils/format";


const TransactionSummary = () => {
  // State to store the data and sort direction

  const [accountBalData, setAccountBalData] = useState(null);
  const [transactionData, setTransactionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [sortColumn, setSortColumn] = useState(null);
  const [clientid, setClientid] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalRecalculationPaymentChangeOpen, setIsModalRecalculationPaymentChangeOpen] = useState(false);

  const [isModalViewDraftDetailsOpen, setIsViewDraftDetailsOpen] = useState(false);

  const [selectedDebt, setSelectedDebt] = useState(null);
  const [calculatorType, setCalculatorType] = useState("1");
  
  const [selectedTableNameFilter, setSelectedTableNameFilter] = useState([]);
  const [uniqueTableNames, setUniqueTableNames] = useState([]);

  const [selectedStatusFilter, setSelectedStatusFilter] = useState([]);
  const [uniqueStatuses, setUniqueStatuses] = useState([]);
  const [filteredUniqueStatuses, setFilteredUniqueStatuses] = useState([]);

  const [selectedDebtIdFilter, setSelectedDebtIdFilter] = useState([]);
  const [uniqueDebtIds, setUniqueDebtIds] = useState([]);
  
  const [sortedData, setSortedData] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [transactionChangeHistoryData, setTransactionChangeHistoryData] = useState([]);
  const [resultHistoryHeaderTrans, setResultHistoryHeaderTrans] = useState([]);
  const [resultHistoryDetailsTrans, setResultHistoryDetailsTrans] = useState([]);

  const [inputValues, setInputValues] = useState({});

  // Handler to clear all filters
  const clearFilters = () => {
    setSelectedTableNameFilter([]);
    setSelectedStatusFilter([]);
    setSelectedDebtIdFilter([]);
    setDateFrom('');
    setDateTo('');
  };

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={2}>
          {typeof children === 'string' ? (
            <Typography>{children}</Typography>
          ) : (
            children
          )}
        </Box>
      )}
    </div>
  );
};

  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  }

  const [resultTrans, setResultTrans] = useState([]);
  //const [originalConsolidatedData, setOriginalConsolidatedData] = useState([]);
  const [consolidatedData, setConsolidatedData] = useState([]);

  // Prepare data for the line chart
  const prepareChartData = () => {
    if (!resultTrans || resultTrans.length === 0) return { series: [], categories: [] };

    const filteredData = resultTrans.filter(item => {
      const date = new Date(item.ClearedDate);
      const balance = Number(item.ForecastEndingBalance).toFixed(2);
      return (
        item.ClearedDate !== 'N/A' &&
        !isNaN(date.getTime()) &&
        !isNaN(balance)
      );
    });

    const sortedData = [...filteredData].sort(
      (a, b) => new Date(a.ClearedDate) - new Date(b.ClearedDate)
    );

    const categories = sortedData.map(item => item.ClearedDate);
    const yValues = sortedData.map(item => Number(item.ForecastEndingBalance));
    return {
      series: [{ data: yValues }],
      categories
    };
  };

 const BalanceLineChart = ({ resultTrans }) => {
  const containerRef = React.useRef(null);
  const [chartDimensions, setChartDimensions] = React.useState({ 
    width: 800, 
    height: 600,
    isMobile: false,
    isTablet: false
  });

  React.useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        const isMobile = width < 600;
        const isTablet = width >= 600 && width < 960;
        
        let height;
        if (isMobile) {
          height = Math.min(300, width * 0.75);
        } else if (isTablet) {
          height = Math.min(400, width * 0.6);
        } else {
          height = Math.min(600, width * 0.5);
        }
        
        setChartDimensions({ width, height, isMobile, isTablet });
      }
    };

    updateDimensions();
    
    let timeoutId;
    const debouncedUpdate = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateDimensions, 150);
    };
    
    window.addEventListener('resize', debouncedUpdate);
    return () => {
      window.removeEventListener('resize', debouncedUpdate);
      clearTimeout(timeoutId);
    };
  }, []);

  const processedTrans = resultTrans
    .filter(item => item.TransactionTitle === 'ACH Client Debit')
    .map(item => ({
      ...item,
      date: new Date(new Date(item.ClearedDate).getTime() + 24 * 60 * 60 * 1000),
    }));

  const generateMonthTicks = (data) => {
    if (data.length === 0) return [];
    const sorted = [...data].sort((a, b) => a.date - b.date);
    const start = new Date(sorted[0].date.getFullYear(), sorted[0].date.getMonth(), 1);
    const end = new Date(sorted[sorted.length - 1].date.getFullYear(), sorted[sorted.length - 1].date.getMonth(), 1);

    const ticks = [];
    let current = new Date(start);
    const skipMonths = chartDimensions.isMobile ? 2 : 1;
    
    while (current <= end) {
      ticks.push(new Date(current));
      current.setMonth(current.getMonth() + skipMonths);
    }
    return ticks;
  };

  const monthTicks = generateMonthTicks(processedTrans);

  const formatDate = (date) => {
    if (chartDimensions.isMobile) {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        year: '2-digit',
      }).format(date);
    } else if (chartDimensions.isTablet) {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
      }).format(date);
    } else {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(date);
    }
  };

  const margins = {
    left: chartDimensions.isMobile ? 50 : 70,
    right: chartDimensions.isMobile ? 10 : 20,
    top: chartDimensions.isMobile ? 10 : 20,
    bottom: chartDimensions.isMobile ? 50 : 70,
  };

  const fontSize = chartDimensions.isMobile ? 10 : chartDimensions.isTablet ? 11 : 12;


// Create a unique class for each status
  const getMarkClass = (index) => {
    if (processedTrans[index]) {
      return processedTrans[index].TransactionStatus === 'COMPLETED' 
        ? 'mark-completed' 
        : 'mark-pending';
    }
    return '';
  };

  return (
    <div 
      ref={containerRef} 
      style={{ 
        width: '97%', 
        height: '100%',
        position: 'relative'
      }}
    >
      <style>
        {`
          .mark-completed circle {
            fill: #1976d2 !important;
            stroke: #1976d2 !important;
            stroke-width: 1.5 !important;
          }
          .mark-pending circle {
            fill: white !important;
            stroke: #1976d2 !important;
            stroke-width: 2 !important;
          }
        `}
      </style>
      <LineChart
        dataset={processedTrans}
        xAxis={[
          {
            scaleType: 'time',
            dataKey: 'date',
            label: chartDimensions.isMobile ? '' : 'Date',
            tickValues: monthTicks,
            valueFormatter: formatDate,
            tickLabelStyle: {
              angle: chartDimensions.isMobile ? -45 : 0,
              textAnchor: chartDimensions.isMobile ? 'end' : 'middle',
              fontSize: fontSize,
            },
          },
        ]}
        yAxis={[
          {
            label: chartDimensions.isMobile ? '' : '',
            tickLabelStyle: {
              fontSize: fontSize,
            },
          },
        ]}
        series={[
          {
            dataKey: 'ForecastEndingBalance',
            label: 'Forecast Saving Account Balance',
            showMark: !chartDimensions.isMobile,
            curve: 'linear',
            color: '#1976d2',
            area: true,
          }
        ]}
        width={chartDimensions.width}
        height={chartDimensions.height}
        margin={margins}
        slotProps={{
          legend: {
            hidden: chartDimensions.isMobile,
          },
        }}
        sx={{
          '& .MuiChartsAxis-tickLabel': {
            fontSize: `${fontSize}px !important`,
          },
          '& .MuiChartsLegend-series': {
            fontSize: `${fontSize}px !important`,
          },
          '& .MuiAreaElement-root': {
            fill: 'url(#balance-gradient)',
            opacity: 0.3,
          },
          // Apply different styles based on data
          ...processedTrans.reduce((acc, item, index) => {
            const isCompleted = item.TransactionStatus === 'COMPLETED';
            acc[`& .MuiMarkElement-root:nth-of-type(${index + 1})`] = {
              fill: isCompleted ? '#31c878ff !important' : 'white !important',
              stroke: isCompleted ? '#31c878ff !important' :'#1976d2 !important',
              strokeWidth: isCompleted ? '1.5px !important' : '2px !important',
            };
            return acc;
          }, {}),
        }}
      >
        <defs>
          <linearGradient id="balance-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1976d2" stopOpacity={0.5} />
            <stop offset="100%" stopColor="#1976d2" stopOpacity={0.05} />
          </linearGradient>
        </defs>
      </LineChart>
    </div>
  );
};


    const getDebtDetailsTransactions = (debtDetailedTrans) => {
      const transLog = [];
      let counterID = 1;

      //  Step 1: Sort input array by ScheduleDateAdjusted (ClearedDate)
      const sortedDebtTrans = [...debtDetailedTrans].sort((a, b) => {
        const dateA = a.ScheduleDateAdjusted ? new Date(a.ScheduleDateAdjusted) : new Date(0); // fallback for nulls
        const dateB = b.ScheduleDateAdjusted ? new Date(b.ScheduleDateAdjusted) : new Date(0);
        return dateA - dateB; // ascending order
      });

      //  Step 2: Process sorted data
      for (let m = 0; m < sortedDebtTrans.length; m++) {
        const item = sortedDebtTrans[m];

        const formattedDateScheduleDateOriginal = item.ScheduleDateOriginal
          ? item.ScheduleDateOriginal.split('T')[0]
          : 'N/A';

        const formattedDateScheduleDateAdjusted = item.ScheduleDateAdjusted
          ? item.ScheduleDateAdjusted.split('T')[0]
          : 'N/A';

        transLog.push({
          CounterID: counterID++,
          DebtID: item.DebtId,
          TransID: item.ID,
          ProcessDate: formattedDateScheduleDateOriginal,
          ClearedDate: formattedDateScheduleDateAdjusted,
          TransactionTitle: item.TransactionTitle,
          OriginalTransactionStatus : item.OriginalTransStatus,
          Amount: item.Amount,
          Type: item.Type,
          TransactionStatus: item.TransactionStatus,
          CurrentEndingBalance: item.CurrentRunningTotal,
          ForecastEndingBalance: item.ForecastRunningTotal,
        });
      }

      return transLog;
    };

    const totalProposedAmount = consolidatedData
      .filter(item => Number(item.ForecastEndingBalance) < 0 && item.TransactionTitle === 'ACH Client Debit' && (item.TransactionStatus ==='SCHEDULED' || item.TransactionStatus ==='PENDING'))
      .reduce((acc, curr) => acc + Number(curr.ProposedAmount || 0), 0);
    const totalUnderFunded = consolidatedData.filter(item => Number(item.ForecastEndingBalance) < 0 && item.TransactionTitle === 'ACH Client Debit' && (item.TransactionStatus ==='SCHEDULED' || item.TransactionStatus ==='PENDING')).length;
    const totalAverageAmount = totalProposedAmount / totalUnderFunded;

    const calculatePaymentChangeTransactions = (debtDetailedTrans) => {
      // Step 1: Sort by ScheduleDateAdjusted
      const sortedDebtTrans = [...debtDetailedTrans].sort((a, b) => {
        return new Date(a.ScheduleDateAdjusted || 0) - new Date(b.ScheduleDateAdjusted || 0);
      });

      // Step 2: Build transaction log with calculations
      let previousFinalizedEndingBalance = 0;
      let previousProposedEndingBalance = 0;
      
      const transLog = sortedDebtTrans.map((item, index) => {
        const isACHDebit = item.TransactionTitle === 'ACH Client Debit';
        const isScheduledOrPending = ['SCHEDULED', 'PENDING'].includes(item.TransactionStatus);
        const isReturnedOrStopped = ['Returned / NSF', 'Stop Payment'].includes(item.OriginalTransStatus);
        const hasNegativeBalance = item.ForecastRunningTotal < 0;

        // Determine proposed and finalized amounts
        let proposedAmount = item.Amount;
        let finalizedAmount = item.Amount;

        if (hasNegativeBalance && isScheduledOrPending) {
          const adjustment = Math.abs(item.ForecastRunningTotal);
          
          if (isACHDebit && !isReturnedOrStopped) {
            // ACH Debit with negative balance - add adjustment
            proposedAmount = item.Amount + adjustment;
            finalizedAmount = item.Amount + adjustment;
          } else if (isACHDebit && isReturnedOrStopped) {
            // ACH Debit returned/stopped - set to 0
            proposedAmount = 0;
            finalizedAmount = 0;
          } else if (!isACHDebit && isReturnedOrStopped) {
            // Non-ACH returned/stopped - set to 0
            proposedAmount = 0;
            finalizedAmount = 0;
          }
          // Non-ACH not returned/stopped keeps default values
        }

        // Calculate proposed ending balance based on previous balance + current transaction
        let proposedNewEndingBalance = previousProposedEndingBalance;
        
        if (isACHDebit) {
          // For ACH Client Debit, always add the proposed amount to previous proposed balance
          proposedNewEndingBalance = previousProposedEndingBalance + proposedAmount;
        } else if (item.Type?.toLowerCase() === 'debit') {
          proposedNewEndingBalance = previousProposedEndingBalance + proposedAmount;
        } else if (item.Type?.toLowerCase() === 'credit') {
          proposedNewEndingBalance = previousProposedEndingBalance - proposedAmount;
        }

        // For ACH Client Debit (not returned/stopped), use proposed amount for finalized amount
        if (isACHDebit && !isReturnedOrStopped) {
          finalizedAmount = proposedAmount;
        }

        // Calculate finalized ending balance based on previous balance + current transaction
        let finalizedNewEndingBalance = previousFinalizedEndingBalance;
        if (item.Type?.toLowerCase() === 'debit') {
          finalizedNewEndingBalance = previousFinalizedEndingBalance + finalizedAmount;
        } else if (item.Type?.toLowerCase() === 'credit') {
          finalizedNewEndingBalance = previousFinalizedEndingBalance - finalizedAmount;
        }

        // Update previous balances for next iteration
        previousProposedEndingBalance = proposedNewEndingBalance;
        previousFinalizedEndingBalance = finalizedNewEndingBalance;

        return {
          CounterID: index + 1,
          DebtID: item.DebtId,
          TransID: item.ID,
          ProcessDate: item.ScheduleDateOriginal?.split('T')[0],
          ClearedDate: item.ScheduleDateAdjusted?.split('T')[0],
          TransactionTitle: item.TransactionTitle,
          Amount: item.Amount,
          Type: item.Type,
          OriginalTransStatus: item.OriginalTransStatus,
          TransactionStatus: item.TransactionStatus,
          CurrentEndingBalance: item.CurrentRunningTotal,
          ForecastEndingBalance: item.ForecastRunningTotal,
          ProposedAmount: proposedAmount,
          ProposedNewEndingBalance: proposedNewEndingBalance,
          FinalizedAmount: finalizedAmount,
          FinalizedNewEndingBalance: finalizedNewEndingBalance
        };
      });

      console.log('Original Consolidated Data Result:', transLog);
      return transLog;
    };

    const recalculatePaymentChangeTransactions = (debtDetailedTrans) => {
      // Returns transactions as-is since calculation is done in useEffect
      console.log('Recalculated Consolidated Data Result:', debtDetailedTrans);
      return debtDetailedTrans;
    };
    const [results, setResults] = useState([]);

    const checkNegativeScheduledTransactions = (transactions) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Compare dates only

      const negativeScheduled = transactions.filter(trans => {
        const status = trans.TransactionStatus?.toUpperCase();
        const clearedDate = trans.ClearedDate !== 'N/A' ? new Date(new Date(trans.ClearedDate).getTime() + 24 * 60 * 60 * 1000) : null;
        const balance = Number(trans.ForecastEndingBalance);
        return status === 'SCHEDULED' && clearedDate && clearedDate > today && balance < 0;
      });

      if (negativeScheduled.length > 0) {
        // Create HTML table string
        const tableRows = negativeScheduled.map(trans => `
          <tr>
            <td>${trans.CounterID}</td>
            <td>${trans.DebtID}</td>
            <td>${trans.TransID}</td>
            <td>${trans.TransactionTitle}</td>
            <td>${trans.ClearedDate}</td>
            <td>${formatCurrencyUSD(Number(trans.ForecastEndingBalance).toFixed(2))}</td>
          </tr>
        `).join('');

        const tableHTML = `
            <table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%; text-align: left;">
              <thead>
                <tr style="background-color: #f0f0f0;">
                  <th>Item #</th>
                  <th>Debt ID</th>
                  <th>Trans ID</th>
                  <th>Transaction</th>
                  <th>Cleared Date</th>
                  <th>Ending Balance</th>
                </tr>
              </thead>
              <tbody>
                ${tableRows}
              </tbody>
            </table>

        `;

        Swal.fire({
          icon: 'warning',
          title: 'Upcoming Negative Balance Alert',
          html: `
            <p>The following upcoming scheduled transactions have negative ending balances:</p>
            ${tableHTML}
          `,
          confirmButtonColor: '#A5CC2D',
           customClass: {
            popup: 'custom-swal-width'
          }
        });
      }
    };

    const getChangeHistoryHeaderTransactions = (changeHistoryHeaderTrans) => {
      const transLog = [];
      let counterID = 1;

      //  Step 1: Sort input array by ScheduleDateAdjusted (ClearedDate)
      const sortedTrans = [...changeHistoryHeaderTrans].sort((a, b) => {
        const dateA = a.App_Created_Date ? new Date(a.App_Created_Date) : new Date(0); // fallback for nulls
        const dateB = b.App_Created_Date ? new Date(b.App_Created_Date) : new Date(0);
        return dateA - dateB; // ascending order
      });

      //  Step 2: Process sorted data
      for (let m = 0; m < sortedTrans.length; m++) {
        const item = sortedTrans[m];

        const formattedCreateDateOriginal = item.App_Created_Date
          ? item.App_Created_Date.split('T')[0]
          : 'N/A';

        // const formattedDateScheduleDateAdjusted = item.ScheduleDateAdjusted
        //   ? item.ScheduleDateAdjusted.split('T')[0]
        //   : 'N/A';

        transLog.push({
          CounterID: counterID++,
          HeaderID: item.PCH_Header_ID,
          TotalCount: item.Total_Count,
          CreatedBy: item.Created_By,
          CreatedDate: formattedCreateDateOriginal
          //TransactionStatus: item.TransactionStatus
        });
      }

      return transLog;
    };


    const getChangeHistoryDetailsTransactions = (changeHistoryDetailsTrans) => {
      const transLog = [];

      //  Step 1: Sort input array by ScheduleDateAdjusted (ClearedDate)
      const sortedTrans = [...changeHistoryDetailsTrans].sort((a, b) => {
        const dateA = a.Created_Date ? new Date(a.Created_Date) : new Date(0); // fallback for nulls
        const dateB = b.Created_Date ? new Date(b.Created_Date) : new Date(0);
        return dateA - dateB; // ascending order
      });

      //  Step 2: Process sorted data
      for (let m = 0; m < sortedTrans.length; m++) {
        const item = sortedTrans[m];

        transLog.push({
          DetailsID : item.PCH_Details_ID,
          HeaderID : item.PCH_Header_ID,
          ItemNo : item.Item_No,
          ContactID : item.Contact_ID,
          ClientID : item.Client_ID,
          ExternalID : item.External_ID,
          ClientName : item.Client_Name,
          DebtID : item.Debt_ID,
          TransID : item.Trans_ID,
          ClearedDate : item.Cleared_Date,
          Transaction : item.Transaction,
          OrigAmount : item.Orig_Amount,
          OrigStatus : item.Orig_Status,
          OrigEndingBalance : item.Orig_Ending_Balance,
          ProposedAmount : item.Proposed_Amount,
          ProposedNewEndingBalance : item.Proposed_New_Ending_Balance,
          FinalizedAmount : item.Finalized_Amount,
          FinalizedNewEndingBalance : item.Finalized_Ending_Balance,
          CreatedBy : item.Created_By,
          CreatedDate : item.Created_Date,
          UpdatedBy : item.Updated_By,
          UpdatedDate : item.Updated_Date,
          CreatedTimeStamp : item.Created_Time_Stamp
        });
      }

      return transLog;
    };


    const [showTable, setShowTable] = useState(false);
    const handleViewDetails = async (headerId) => {
      try {
        setShowTable(false); //  Hide the table immediately
        setResultHistoryDetailsTrans([]); // Clear previous data
        setIsViewDraftDetailsOpen(true);
        setIsVisible(true);

        const response = await fetch(`https://eozaol3a92fwx3h.m.pipedream.net/api/PaymentChangeHistory/GetAllPaymentChangeDetailsByHeaderID?headerid=${headerId}`);
        const data = await response.json();

        const allocationHistoryTrans = getChangeHistoryDetailsTransactions(data);
        setResultHistoryDetailsTrans(allocationHistoryTrans);

        setIsVisible(false);
        setShowTable(true); //  Show the table after data is loaded
      } catch (error) {
        console.error('Error fetching header details:', error);
        setIsVisible(false);
      }
    };



    const handleClick = () => {
      // const allocation = calculateFeeAllocation();
      // setResults(allocation);
      Swal.fire({
              title: "Processing...",
              text: `Sample`,
              timer: 1500,
              showConfirmButton: false,
              willClose: () => {}, // optional callback
              })
    };


    const handleClickTransactions = () => {
      const allocation = getDebtDetailsTransactions(transactionData);
      setResultTrans(allocation);
    };


    // const handleClickChangePaymentTransactions = () => {
    //   const allocation = recalculatePaymentChangeTransactions(consolidatedData);
    //     setConsolidatedData(allocation);
    // };

    const handleInputChange = (transId, value) => {
      const newAmount = Number(value);

      // 1. Update input values
      const updatedInputValues = {
        ...inputValues,
        [transId]: newAmount,
      };
      setInputValues(updatedInputValues);

      //  update logic will be triggered automatically via useEffect
    };

  useEffect(() => {
    // Find which transaction was changed
    let changedTransId = null;
    
    for (const transId in inputValues) {
      changedTransId = transId;
      break; // Get the most recent change
    }

    // Step 1: Update the FinalizedAmount for all rows (use input or ProposedAmount)
    const updatedTransactions = consolidatedData.map((item) => {
      const inputValue = inputValues[item.TransID];
      
      // Use inputValue if provided, otherwise use ProposedAmount
      const finalizedAmount = inputValue != null && !isNaN(inputValue)
        ? inputValue
        : item.ProposedAmount;
      
      const previousEndingBalance = item.ForecastEndingBalance - item.Amount;
      
      return {
        ...item,
        FinalizedAmount: finalizedAmount,
        FinalizedNewEndingBalance: previousEndingBalance + finalizedAmount,
      };
    });

    // Step 2: Sort by ClearedDate to ensure correct chronological order
    const sortedTransactions = [...updatedTransactions].sort((a, b) => {
      const dateA = new Date(a.ClearedDate || 0);
      const dateB = new Date(b.ClearedDate || 0);
      return dateA - dateB;
    });

    // Step 3: Find the index of the changed transaction in sorted array
    const sortedChangedIndex = changedTransId 
      ? sortedTransactions.findIndex(item => item.TransID === changedTransId)
      : -1;

    // Step 4: Recalculate FinalizedNewEndingBalance from the changed row forward
    if (sortedChangedIndex !== -1) {
      for (let i = sortedChangedIndex + 1; i < sortedTransactions.length; i++) {
        const currentItem = sortedTransactions[i];
        const previousItem = sortedTransactions[i - 1];
        
        // Skip COMPLETED transactions - keep their original EndingBalance logic
        if (currentItem.TransactionStatus === 'COMPLETED') {
          currentItem.FinalizedNewEndingBalance = currentItem.ForecastEndingBalance;
          continue;
        }
        
        // For non-COMPLETED transactions: recalculate based on Type
        // If Type is "CREDIT", subtract; otherwise add
        if (currentItem.Type === 'Credit') {
          currentItem.FinalizedNewEndingBalance = 
            previousItem.FinalizedNewEndingBalance - currentItem.FinalizedAmount;
        } else {
          currentItem.FinalizedNewEndingBalance = 
            previousItem.FinalizedNewEndingBalance + currentItem.FinalizedAmount;
        }
      }
    } else {
      // If no specific change detected, recalculate all rows in sequence
      for (let i = 0; i < sortedTransactions.length; i++) {
        const currentItem = sortedTransactions[i];
        
        if (currentItem.TransactionStatus === 'COMPLETED') {
          currentItem.FinalizedNewEndingBalance = currentItem.ForecastEndingBalance;
          continue;
        }
        
        if (i === 0) {
          // First row: calculate based on Type
          const previousEndingBalance = currentItem.ForecastEndingBalance - currentItem.Amount;
          if (currentItem.Type === 'Credit') {
            currentItem.FinalizedNewEndingBalance = 
              previousEndingBalance - currentItem.FinalizedAmount;
          } else {
            currentItem.FinalizedNewEndingBalance = 
              previousEndingBalance + currentItem.FinalizedAmount;
          }
          continue;
        }
        
        const previousItem = sortedTransactions[i - 1];
        
        // Apply Type logic for subsequent rows
        if (currentItem.Type === 'Credit') {
          currentItem.FinalizedNewEndingBalance = 
            previousItem.FinalizedNewEndingBalance - currentItem.FinalizedAmount;
        } else {
          currentItem.FinalizedNewEndingBalance = 
            previousItem.FinalizedNewEndingBalance + currentItem.FinalizedAmount;
        }
      }
    }

    // Step 5: Apply the recalculation function (if needed for other logic)
    const allocation = recalculatePaymentChangeTransactions(sortedTransactions);
    setConsolidatedData(allocation);
    
    console.log('Final Transactions Data Result:', allocation);
  }, [inputValues]);

    const handleClickSaveDraft = async () => {
      try {
        //  Step 1: Validation check
        const missingRequired = consolidatedData.some(item => {
          const isEditable =  item.TransactionTitle === 'ACH Client Debit';
          const finalizedAmount = inputValues[item.TransID] ?? item.FinalizedAmount;

          return isEditable && (finalizedAmount == null || finalizedAmount === ''  || finalizedAmount === undefined);
        });

        if (missingRequired) {
          Swal.fire({
            icon: 'warning',
            title: 'Check All Finalized Amount',
            text: 'Please fill in all Finalized Amounts with correct data before saving. Invalid amount is not allowed.',
            confirmButtonColor: '#A5CC2D',
          });
          return;
        }

        //  Step 2: Proceed with saving draft
        const userData = localStorage.getItem('user');
        const parsedUser = JSON.parse(userData);

        console.log("Setting before draftItems based from consolidatedData:", consolidatedData);

        const draftItems = consolidatedData
          .filter((item) => 
            item.TransactionTitle === 'ACH Client Debit' && 
            (item.TransactionStatus === 'SCHEDULED' || item.TransactionStatus === 'PENDING')
          )
          .map((item, index) => ({
            Item_No: item.CounterID || null,
            Contact_ID: forthid,
            Client_ID: cftid,
            External_ID: dmid,
            Client_Name: clientname,
            Debt_ID: item.DebtID,
            Trans_ID: item.TransID,
            Cleared_Date: item.ClearedDate,
            Transaction: item.TransactionTitle,
            Orig_Amount: item.Amount,
            Orig_Status: item.TransactionStatus,
            Orig_Ending_Balance: item.ForecastEndingBalance,
            Proposed_Amount: item.ProposedAmount,
            Proposed_New_Ending_Balance: item.ProposedNewEndingBalance,
            Finalized_Amount: item.FinalizedAmount,
            Finalized_Ending_Balance: item.FinalizedNewEndingBalance,
            Created_By: parsedUser.userId,
            Created_Date: new Date().toISOString(),
            Updated_By: parsedUser.userId,
            Updated_Date: new Date().toISOString()
        }));

        console.log("Setting draftItems:", draftItems);

        if (draftItems.length === 0) {
          Swal.fire({
            icon: 'warning',
            title: 'No Data to Save',
            text: 'There is no data available to save as a draft.',
            confirmButtonColor: '#A5CC2D',
          });
          return;
        }

        const payload = JSON.stringify(draftItems);
        setIsVisible(true);

        const response = await fetch('https://eohwbhv27a62oan.m.pipedream.net/api/PaymentChangeHistory/CreateNewDraftHeaderDetails', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
        });

        if (!response.ok) throw new Error('Failed to save draft');

        const result = await response.json();
        setIsVisible(false);

        Swal.fire({
          icon: 'success',
          title: 'Draft Saved',
          text: 'Your changes have been saved as a draft.',
          confirmButtonColor: '#A5CC2D',
        }).then(() => {
          fetchPaymentChangeHeaders(); // Refresh data
        });

      } catch (err) {
        console.error('Error saving draft:', err);
        setIsVisible(false);
        Swal.fire({
          icon: 'error',
          title: 'Save Failed',
          text: 'Could not save the draft. Please try again.',
          confirmButtonColor: '#A5CC2D',
        });
      }
    };



  const fetchPaymentChangeHeaders = async () => {
    try {
      setIsVisible(true); // Show loading data section
      const response = await fetch(`https://eohpz9errvqpoy8.m.pipedream.net/api/PaymentChangeHistory/GetAllPaymentChangeHeaders?clientid=${clientid}`);
      if (!response.ok) throw new Error('Failed to fetch payment change headers');
      
      const data = await response.json();

      
      const allocationHistoryTrans = getChangeHistoryHeaderTransactions(data);
      setResultHistoryHeaderTrans(allocationHistoryTrans);
      setIsVisible(false); // Show loading data section
    } catch (err) {
      console.error('Error fetching payment change headers:', err);
    }
  };
    

  const Modal = ({ isOpen, onClose, children }) => {
    if (!isOpen) return null;

    return (
      <div className="calcu-modal-overlay">
        <div className="modal modal-md">
          <button className="modal-close" onClick={onClose}>×</button>
          <div className="modal-content">{children}</div>
        </div>
      </div>
    );
  };

  const DebtIDFilterDropdown = ({ selectedFilter, onChange, options }) => {
    const [isOpen, setIsOpen] = useState(false);
    const handleCheckboxChange = (option, isChecked) => {
      if (isChecked) {
        onChange([...selectedFilter, option]);
      } else {
        onChange(selectedFilter.filter(item => item !== option));
      }
    };

    return (
      <div style={{ display: 'inline-flex', flexDirection: 'column', minWidth: '140px', marginBottom: '10px', marginLeft: '10px', position: 'relative' }}>
        <label style={{ fontSize: '1em', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
          Filter by Debt ID
        </label>
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            height: '40px',
            padding: '10px',
            border: '1px solid #D1D5DB',
            borderRadius: '7px',
            backgroundColor: 'white',
            cursor: 'pointer',
            width: '200px',
            textAlign: 'left',
            fontSize: '1em',
            fontWeight: '500',
            color: '#374151'
          }}
        >
          Debt ID {selectedFilter.length > 0 ? `(${selectedFilter.length})` : ''}
        </button>
        {isOpen && (
          <div
            style={{
              border: '1px solid #D1D5DB',
              borderRadius: '7px',
              padding: '10px',
              backgroundColor: 'white',
              width: '150px',
              maxHeight: '150px',
              overflowY: 'auto',
              position: 'absolute',
              top: '100%',
              left: '0',
              zIndex: 1000
            }}
          >
            {options.map(option => (
              <div key={option} style={{ marginBottom: '5px' }}>
                <label style={{ display: 'flex', alignItems: 'center', fontSize: '1em', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedFilter.includes(option)}
                    onChange={(e) => handleCheckboxChange(option, e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  {option}
                </label>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const TableNameFilterDropdown = ({ selectedFilter, onChange, options }) => {
    const [isOpen, setIsOpen] = useState(false);
    const handleCheckboxChange = (option, isChecked) => {
      if (isChecked) {
        onChange([...selectedFilter, option]);
      } else {
        onChange(selectedFilter.filter(item => item !== option));
      }
    };

    return (
      <div style={{ display: 'inline-flex', flexDirection: 'column', minWidth: '140px', marginBottom: '10px', marginLeft: '10px', position: 'relative' }}>
        <label style={{ fontSize: '1em', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
          Filter by Transaction
        </label>
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            height: '40px',
            padding: '10px',
            border: '1px solid #D1D5DB',
            borderRadius: '7px',
            backgroundColor: 'white',
            cursor: 'pointer',
            width: '250px',
            textAlign: 'left',
            fontSize: '1em',
            fontWeight: '500',
            color: '#374151'
          }}
        >
          Transaction {selectedFilter.length > 0 ? `(${selectedFilter.length})` : ''}
        </button>
        {isOpen && (
          <div
            style={{
              border: '1px solid #D1D5DB',
              borderRadius: '7px',
              padding: '10px',
              backgroundColor: 'white',
              width: '250px',
              maxHeight: '150px',
              overflowY: 'auto',
              position: 'absolute',
              top: '100%',
              left: '0',
              zIndex: 1000
            }}
          >
            {options.map(option => (
              <div key={option} style={{ marginBottom: '5px' }}>
                <label style={{ display: 'flex', alignItems: 'center', fontSize: '1em', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedFilter.includes(option)}
                    onChange={(e) => handleCheckboxChange(option, e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  {option}
                </label>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };


  const StatusFilterDropdown = ({ selectedFilter, onChange, options }) => {
    const [isOpen, setIsOpen] = useState(false);
    const handleCheckboxChange = (option, isChecked) => {
      if (isChecked) {
        onChange([...selectedFilter, option]);
      } else {
        onChange(selectedFilter.filter(item => item !== option));
      }
    };

    return (
      <div style={{ display: 'inline-flex', flexDirection: 'column', minWidth: '140px', marginBottom: '10px', marginLeft: '10px', position: 'relative' }}>
        <label style={{ fontSize: '1em', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
          Filter by Overall Status
        </label>
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            height: '40px',
            padding: '10px',
            border: '1px solid #D1D5DB',
            borderRadius: '7px',
            backgroundColor: 'white',
            cursor: 'pointer',
            width: '200px',
            textAlign: 'left',
            fontSize: '1em',
            fontWeight: '500',
            color: '#374151'
          }}
        >
          Statuses {selectedFilter.length > 0 ? `(${selectedFilter.length})` : ''}
        </button>
        {isOpen && (
          <div
            style={{
              border: '1px solid #D1D5DB',
              borderRadius: '7px',
              padding: '10px',
              backgroundColor: 'white',
              width: '150px',
              maxHeight: '150px',
              overflowY: 'auto',
              position: 'absolute',
              top: '100%',
              left: '0',
              zIndex: 1000
            }}
          >
            {options.map(option => (
              <div key={option} style={{ marginBottom: '5px' }}>
                <label style={{ display: 'flex', alignItems: 'center', fontSize: '1em', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedFilter.includes(option)}
                    onChange={(e) => handleCheckboxChange(option, e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  {option}
                </label>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const filterByDateRange = (data, from, to) => {
    if (!from && !to) return data;
    return data.filter(item => {
      if (item.ClearedDate === 'N/A') return false;
      const date = new Date(item.ClearedDate);
      const fromDate = from ? new Date(from) : null;
      const toDate = to ? new Date(to) : null;
      if (fromDate && date < fromDate) return false;
      if (toDate && date > toDate) return false;
      return true;
    });
  };


  const [cftid, setCftid] = useState('');
  const [dmid, setDmid] = useState('');
  const [forthid, setForthid] = useState('');
  const [clientname, setClientname] = useState('');
  
  // Check for client-id continuously until it is found
  useEffect(() => {
  const observeChanges = () => {
    const cftidElement = document.getElementById('client-id');
    const dmidElement = document.getElementById('external-id');
    const forthidElement = document.getElementById('contact-id');
    const clientNameElement = document.getElementById('client-name');
    const balanceIdElement = document.getElementById('balance-id');

    const getValidClientId = () => {
      const newCftid = cftidElement?.innerText.trim() || '';
      const newDmid = dmidElement?.innerText.trim() || '';
      const newForthid = forthidElement?.innerText.trim() || '';
      const newClientname = clientNameElement?.innerText.replace(/ACTIVE/i, '').trim() || '';

      // Update states
      setCftid(newCftid);
      setDmid(newDmid);
      setForthid(newForthid);
      setClientname(newClientname);

      const ids = [newForthid, newCftid, newDmid];
      return ids.find(id => id && id !== '0' && id !== 0) || 0;
    };

    const handleChange = () => {
      const foundClientid = getValidClientId();

      if (foundClientid && foundClientid !== '0') {
        setClientid(foundClientid);

        if (balanceIdElement) {
          const foundBalance = Number(
            balanceIdElement.innerText.trim().replace('$', '').replace(/,/g, '')
          );
          setAccountBalData(foundBalance);
        }
      }
    };

    const observer = new MutationObserver(handleChange);
    const config = { childList: true, subtree: true, characterData: true };

    if (cftidElement) observer.observe(cftidElement, config);
    if (dmidElement) observer.observe(dmidElement, config);
    if (forthidElement) observer.observe(forthidElement, config);

    return () => {
      observer.disconnect(); // Clean up when component unmounts
    };
  };

  observeChanges();
}, []);


  // Fetch data once the client-id is available
  useEffect(() => {
    if (!clientid || clientid === '0') return; // Do not fetch until clientid is found
    
      const fetchData = async () => {
        try {
          //setLoading(true);
          setIsVisible(true); // Show loading data section
          const [transRes,changeHistoryRes] = await Promise.all([
            fetch(`https://eobetf26kzfxj10.m.pipedream.net/api/PaymentChangeCalculator/GetAllTransactionsByClientID?clientid=${clientid}`),
            fetch(`https://eohpz9errvqpoy8.m.pipedream.net/api/PaymentChangeHistory/GetAllPaymentChangeHeaders?clientid=${clientid}`)
          ]);
  
          const transactionData =  await transRes.json();
          const transactionChangeHistoryData =  await changeHistoryRes.json();

          setTransactionData(transactionData);
          const allocation = calculatePaymentChangeTransactions(transactionData);
          setConsolidatedData(allocation);

          const allocationTrans = getDebtDetailsTransactions(transactionData);
          setResultTrans(allocationTrans);
          checkNegativeScheduledTransactions(allocationTrans);

          //setTransactionChangeHistoryData(transactionChangeHistoryData);
          const allocationHistoryTrans = getChangeHistoryHeaderTransactions(transactionChangeHistoryData);
          setResultHistoryHeaderTrans(allocationHistoryTrans);


          setError(null);
        } catch (err) {
            setError(err);
            Swal.fire({
              icon: "error",
              title: "We're unable to get the debt summary data.",
              text: "Kindly contact PDR Support for assistance.",
              confirmButtonColor: '#A5CC2D', 
            });
        } finally {
          setLoading(false);
        }
  
        setIsVisible(false); // Show loading data section
      };
  
      fetchData();
  }, [clientid]); // Trigger when clientid changes

   useEffect(() => {
      const uniqueTrans = [...new Set(resultTrans.map(r => r.TransactionTitle).filter(Boolean))];
      console.log("Setting uniqueTableNames:", uniqueTrans);
      setUniqueTableNames(uniqueTrans);
    }, [resultTrans]);
  
    useEffect(() => {
      let filtered = resultTrans;
      if (selectedTableNameFilter.length > 0) {
        filtered = filtered.filter(r => selectedTableNameFilter.includes(r.TransactionTitle));
      }
      if (selectedStatusFilter.length > 0) {
        filtered = filtered.filter(r => selectedStatusFilter.includes(r.TransactionStatus));
      }
      if (selectedDebtIdFilter.length > 0) {
        filtered = filtered.filter(r => selectedDebtIdFilter.includes(r.DebtID));
      }
      filtered = filterByDateRange(filtered, dateFrom, dateTo);
      console.log("Setting combined filtered data:", filtered);
      setSortedData(filtered);
    }, [selectedTableNameFilter,selectedDebtIdFilter, selectedStatusFilter, dateFrom, dateTo, resultTrans]);
  
  
    useEffect(() => {
  
      const uniqueStats= [...new Set(resultTrans.map(r => r.TransactionStatus).filter(Boolean))];
      console.log("Setting uniqueStatuses:", uniqueStats);
      setUniqueStatuses(uniqueStats);
    }, [resultTrans]);
  
    useEffect(() => {
  
      const uniqueDebtId= [...new Set(resultTrans.map(r => r.DebtID).filter(Boolean))];
      console.log("Setting uniqueDebtIds:", uniqueDebtId);
      setUniqueDebtIds(uniqueDebtId);
    }, [resultTrans]);
  
    useEffect(() => {
      if (selectedTableNameFilter.length === 0) {
        setFilteredUniqueStatuses(uniqueStatuses);
      } else {
        const filtered = resultTrans
          .filter(r => selectedTableNameFilter.includes(r.TransactionTitle))
          .map(r => r.TransactionStatus)
          .filter(Boolean);
        const uniqueFiltered = [...new Set(filtered)];
        setFilteredUniqueStatuses(uniqueFiltered);
      }
    }, [selectedTableNameFilter, resultTrans, uniqueStatuses]);



  // Column mapping for sorting
  const columnMapping = {
    'CounterID': 'CounterID',
    'DebtID': 'DebtID',
    'TransID': 'TransID',
    'ProcessDate': 'ProcessDate',
    'ClearedDate': 'ClearedDate',
    'Transaction': 'TransactionTitle',
    'Amount': 'Amount',
    'Status': 'TransactionStatus',
    'EndingBalance': 'EndingBalance'
  };

  // Function to handle sorting
  const handleSort = (column) => {
    const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    setSortDirection(newDirection);
    setSortColumn(column);

    const actualColumn = columnMapping[column];

    const sortedData = [...resultTrans].sort((a, b) => {
      let aVal = a[actualColumn];
      let bVal = b[actualColumn];

      // Handle 'N/A' as null
      if (aVal === 'N/A') aVal = null;
      if (bVal === 'N/A') bVal = null;

      // Type-specific handling
      if (actualColumn === 'ProcessDate' || actualColumn === 'ClearedDate') {
        aVal = aVal ? new Date(aVal) : new Date(0); // Old date for 'N/A'
        bVal = bVal ? new Date(bVal) : new Date(0);
      } else if (actualColumn === 'Amount' || actualColumn === 'EndingBalance') {
        aVal = aVal ? parseFloat(aVal) : 0;
        bVal = bVal ? parseFloat(bVal) : 0;
      } else {
        // Strings
        aVal = aVal || '';
        bVal = bVal || '';
      }

      if (newDirection === 'asc') {
        if (aVal < bVal) return -1;
        if (aVal > bVal) return 1;
        return 0;
      } else {
        if (aVal > bVal) return -1;
        if (aVal < bVal) return 1;
        return 0;
      }
    });

    setResultTrans(sortedData);
  };

  // Function to handle sorting
  const handleSortChangeHistory = (column) => {
    const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    setSortDirection(newDirection);
    setSortColumn(column);

    const actualColumn = columnMapping[column];

    const sortedData = [...resultHistoryHeaderTrans].sort((a, b) => {
      let aVal = a[actualColumn];
      let bVal = b[actualColumn];

      // Handle 'N/A' as null
      if (aVal === 'N/A') aVal = null;
      if (bVal === 'N/A') bVal = null;

      // Type-specific handling
      if (actualColumn === 'CreatedDate') {
        aVal = aVal ? new Date(aVal) : new Date(0); // Old date for 'N/A'
        bVal = bVal ? new Date(bVal) : new Date(0);
      } else {
        // Strings
        aVal = aVal || '';
        bVal = bVal || '';
      }

      if (newDirection === 'asc') {
        if (aVal < bVal) return -1;
        if (aVal > bVal) return 1;
        return 0;
      } else {
        if (aVal > bVal) return -1;
        if (aVal < bVal) return 1;
        return 0;
      }
    });

    setResultHistoryHeaderTrans(sortedData);
  };

  return (
    <div className="debt-summary-trans-table">
      <div className="flex-container">
        <div className="column">
          <label className="container-headtitle">Transaction Summary</label>
          <select
            className="form-control ddl"
            id="ddl-calculator-type"
            value={calculatorType}
            onChange={(e) => setCalculatorType(e.target.value)}
          >
            <option value="1">Payment Change Calculator</option>
          </select>
        
          {calculatorType === "1" && (
            <button
              id="btn-view-transactions"
              onClick={() => {
                handleClickTransactions();
              }}
              className="btn btn-secondary"
            >
              Reload Data
            </button>
          )}
          {calculatorType === "1" && (
            <button
              id="btn-recalculate-payment"
              onClick={() => {
                setIsModalRecalculationPaymentChangeOpen(true);
                setResults([]);
              }}
              className="btn btn-secondary"
            >
              Recalculate Payment
            </button>
          )}
          {isVisible && (<span className="spinner"></span>)}
          <br />
          <br />
          <br />

          <Modal isOpen={isModalRecalculationPaymentChangeOpen} onClose={() => setIsModalRecalculationPaymentChangeOpen(false)}>
            <h2>Payment Change Recalculation</h2>
            <hr/>
   
            <div className="dashboard">
                <div className="flex-container">
                    <div className="column">
                        <label className="container-title">Overview 📝</label>
                        <label className="container-descr">
                          Payment change recalculation automatically generates a proposed amount to prevent negative balances for all transactions (Deposit / ACH Client Debit) with scheduled or pending statuses.<br/><br/>
                          The app module supports saving multiple entries from different users and saved drafts can be viewed in Change History Tab.
                        </label>
                    </div>
                    <div className="column">
                        <label className="container-title">Reminder 📌</label>
                        <label className="container-descr">✅ Initial Finalized Amount displayed is based from proposed amount</label>
                        <label className="container-descr">✅ Make sure each row under Finalized Amount column has correct amount</label>
                        <label className="container-descr">✅ Make sure each row under Finalized New Ending Balance column is not negative</label>
                        <label className="container-descr">✅ Check all Finalized Amounts before draft saving</label>
                    </div>
                    
                </div>
              </div>
            {/* <button id="btn-calculate-payment-change" className="btn btn-secondary"  onClick={handleClickChangePaymentTransactions} >Calculate</button> */}
            <button id="btn-save-changes" className="btn btn-secondary" onClick={handleClickSaveDraft} >Save Draft</button><br/><br/>
            {isVisible && (<span className="spinner"></span>)}

            
              <div className="dashboard">
                <div className="flex-container">
                    <div className="column">
                        <label className="container-title">Total Funds Needed</label>
                        <label 
                        className="container-count" 
                        id="balance-id"  
                        style={{
                           // color: data.Balance < 0? 'red' : 'green',
                            }}
                        >{formatCurrencyUSD(Number(totalProposedAmount.toFixed(2)))}</label>
                    </div>
                    <div className="column">
                        <label className="container-title">Number of Drafts Under Funded</label>
                        <label 
                        className="container-count" 
                        id="balance-id"  
                        style={{
                            //color: data.Balance < 0? 'red' : 'green',
                            }}
                        >{Number(totalUnderFunded.toFixed(2))}</label>
                    </div>
                    <div className="column">
                        <label className="container-title">Average Draft Needed</label>
                        <label 
                        className="container-count" 
                        id="balance-id"  
                        style={{
                            //color: data.Balance < 0? 'red' : 'green',
                            }}
                        >{formatCurrencyUSD(Number(totalAverageAmount.toFixed(2)))}</label>
                    </div>
                </div>
              </div>
              <br />
             <div className="table-container table-sm">
                  {consolidatedData.length > 0 && (
                    <table className="table-bordered scrollable-table">
                      <thead>
                        <tr>
                          <th onClick={() => handleSort('CounterID')} title="Sort by Item #">Item # {sortColumn === 'CounterID' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('DebtID')} title="Sort by Debt ID">Debt ID {sortColumn === 'DebtID' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('TransID')} title="Sort by Trans ID ">Trans ID {sortColumn === 'TransID' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('ClearedDate')} title="Sort by Cleared Date">Cleared Date {sortColumn === 'ClearedDate' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('Transaction')} title="Sort by Transaction">Transaction {sortColumn === 'Transaction' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('Amount')} title="Sort by Amount">Amount {sortColumn === 'Amount' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('OrigTransStatus')} title="Sort by Original Status">Original Status {sortColumn === 'Original Status' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('OverallStatus')} title="Sort by Overall Status">Overall Status {sortColumn === 'Overall Status' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('EndingBalance')} title="Sort by Ending Balance">Ending Balance {sortColumn === 'EndingBalance' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('ProposedAmount')} title="Sort by Proposed Amount">Proposed Amount {sortColumn === 'ProposedAmount' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('ProposedNewEndingBalance')} title="Sort by Proposed New Ending Balance">Proposed New Ending Balance {sortColumn === 'ProposedNewEndingBalance' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('FinalizedAmount')} title="Sort by Actual Amount">Finalized Amount {sortColumn === 'FinalizedAmount' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('FinalizedNewEndingBalance')} title="Sort by Actual New Ending Balance">Finalized New Ending Balance {sortColumn === 'FinalizedNewEndingBalance' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        </tr>
                      </thead>
                     <tbody>
                      {consolidatedData.length > 0 ? (
                        consolidatedData
                          .filter((row) => row.TransactionTitle === 'ACH Client Debit' && (row.TransactionStatus !== 'COMPLETED')) // Add filter here
                          .map((row) => (
                          <tr
                            key={row.CounterID}
                            style={{
                              backgroundColor:
                                row.EndingBalance < 0 
                                ?  '#ffeaeaff' 
                                : row.TransactionTitle === 'ACH Client Debit'
                                ?  '#ecffeaff' 
                                : 'inherit',
                            }}
                            data-trans-id={row.TransID || 'N/A'}
                          >
                            <td>{row.CounterID}</td>
                            <td>{row.DebtID || 'N/A'}</td>
                            <td>{row.TransID || 'N/A'}</td>
                            <td>{row.ClearedDate || 'N/A'}</td>
                            <td>{row.TransactionTitle || 'N/A'}</td>
                             <td>
                              <span
                                style={{
                                  color: 
                                      row.Type?.toUpperCase() === "CREDIT" 
                                      ? 'red' 
                                      : row.Type?.toUpperCase() === "DEBIT"
                                      ? 'green'
                                      : 'purple',
                                }}
                              >
                                {
                                  row.Type?.toUpperCase() === "CREDIT"  
                                  ? ' - ' 
                                  : row.Type?.toUpperCase() === "DEBIT"
                                  ? ' + ' 
                                  : ''
                                } 
                              {formatCurrencyUSD(Number(row.Amount || '0.00').toFixed(2))}
                              </span>
                            </td>
                             <td>
                                {row.OriginalTransStatus || 'N/A'}
                            </td>
                            <td>
                              <span
                                className="badge"
                                style={{
                                  backgroundColor:
                                    row.TransactionStatus?.toUpperCase() === 'COMPLETED'
                                    ? '#b7ffd8ff'
                                    : row.TransactionStatus?.toUpperCase() === 'SCHEDULED'
                                    ? '#ffebb7ff'
                                    : 'inherit',
                                }}
                              >
                                {row.TransactionStatus || 'N/A'}
                              </span>
                            </td>
                            <td>
                              <span
                                style={{
                                  color: row.ForecastEndingBalance < 0 ? 'red' : 'green',
                                }}
                                title={
                                    row.Type?.toUpperCase() === "CREDIT"  
                                    ? 'Ending Balance - Decreased'
                                    : row.Type?.toUpperCase() === "DEBIT"
                                    ? 'Ending Balance - Increased'
                                    : 'Ending Balance - No Changes'
                                  }
                              >
                                {formatCurrencyUSD(
                                  Number(row.ForecastEndingBalance || '0.00').toFixed(2)
                                )}{' '}
                                <span
                                  style={{
                                    color: 
                                    row.Type?.toUpperCase() === "CREDIT"  
                                    ? 'red' 
                                    : row.Type?.toUpperCase() === "DEBIT"
                                    ? 'green' 
                                    : 'purple',
                                  }}
                                >
                                  {
                                  row.Type?.toUpperCase() === "CREDIT"  
                                  ? '⬇︎' 
                                  : row.Type?.toUpperCase() === "DEBIT"
                                  ? '⬆︎' 
                                  : '➖'
                                  } 
                                </span>
                              </span>
                            </td>

                            <td>
                              <span
                               style={{
                                  color: row.Type === "credit" || row.Type === "Credit" ? 'red' : 'blue',
                                }}
                              >
                                {
                                  row.Type?.toUpperCase() === "CREDIT"  
                                  ? ' - ' 
                                  : row.Type?.toUpperCase() === "DEBIT"
                                  ? ' + ' 
                                  : ''
                                } 
                                {formatCurrencyUSD(Number(row.ProposedAmount || '0.00').toFixed(2))}
                              </span>
                              </td> 
                            <td>
                              <span
                                style={{
                                  color: row.ProposedNewEndingBalance < 0 ? 'red' : 'green',
                                }}
                                title={
                                    row.Type?.toUpperCase() === "CREDIT"  
                                    ? 'Ending Balance - Decreased'
                                    : row.Type?.toUpperCase() === "DEBIT"
                                    ? 'Ending Balance - Increased'
                                    : 'Ending Balance - No Changes'
                                  }
                              >
                                {formatCurrencyUSD(
                                  Number(row.ProposedNewEndingBalance || '0.00').toFixed(2)
                                )}{' '}
                                <span
                                  style={{
                                    color: 
                                    row.Type?.toUpperCase() === "CREDIT"  
                                    ? 'red' 
                                    : row.Type?.toUpperCase() === "DEBIT"
                                    ? 'green' 
                                    : 'purple',
                                  }}
                                >
                                  {
                                  row.Type?.toUpperCase() === "CREDIT"  
                                  ? '⬇︎' 
                                  : row.Type?.toUpperCase() === "DEBIT"
                                  ? '⬆︎' 
                                  : '➖'
                                  } 
                                </span>
                              </span>
                            </td>
                            <td>
                              <input
                                  type="number"
                                  min={0} //  ensures value can't go below zero
                                  step="0.01" 
                                  required={
                                    row.TransactionTitle === 'ACH Client Debit'
                                  }
                                  readOnly={
                                    row.TransactionStatus === 'COMPLETED' || row.TransactionTitle != 'ACH Client Debit'
                                  }
                                  style={{
                                    height: '20px',
                                    width: '70px',
                                    padding: '5px 10px',
                                    borderRadius: '4px',
                                    border: 
                                      row.TransactionTitle === 'ACH Client Debit'
                                        ? '1px solid'
                                        : '1px #f0f0f0',
                                    backgroundColor:
                                      row.TransactionTitle === 'ACH Client Debit'
                                        ? 'white'
                                        : '#f0f0f0', // Optional: visually indicate it's disabled
                                  }}
                                  // hidden={
                                  //   !(
                                  //     row.TransactionTitle === 'ACH Client Debit'
                                  //   )
                                  // }
                                  value={
                                      row.TransactionStatus === 'COMPLETED' && (row.OriginalTransStatus !='Returned / NSF' || row.OriginalTransStatus !='Stop Payment')
                                      ? row.Amount
                                      : row.TransactionStatus === 'COMPLETED' && (row.OriginalTransStatus ==='Returned / NSF' || row.OriginalTransStatus ==='Stop Payment')
                                      ? '0'
                                      : inputValues[row.TransID] ?? row.ProposedAmount
                                  }
                                  onChange={(e) => handleInputChange(row.TransID, e.target.value)}
                                />
                
                            </td>
                            <td>
                              <span
                                style={{
                                  color: row.FinalizedNewEndingBalance < 0 ? 'red' : 'green',
                                }}
                                title={
                                    row.Type?.toUpperCase() === "CREDIT"  
                                    ? 'Ending Balance - Decreased'
                                    : row.Type?.toUpperCase() === "DEBIT"
                                    ? 'Ending Balance - Increased'
                                    : 'Ending Balance - No Changes'
                                  }
                              >
                                {formatCurrencyUSD(
                                  Number(row.FinalizedNewEndingBalance).toFixed(2)
                                )}{' '}
                                <span
                                  style={{
                                    color: 
                                    row.Type?.toUpperCase() === "CREDIT"  
                                    ? 'red' 
                                    : row.Type?.toUpperCase() === "DEBIT"
                                    ? 'green' 
                                    : 'purple',
                                  }}
                                >
                                  {
                                  row.Type?.toUpperCase() === "CREDIT"  
                                  ? '⬇︎' 
                                  : row.Type?.toUpperCase() === "DEBIT"
                                  ? '⬆︎' 
                                  : '➖'
                                  } 
                                </span>
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9">No data available</td>
                        </tr>
                      )}
                    </tbody>

                    </table>
                  )}
            </div>
        </Modal>

        <Modal isOpen={isModalViewDraftDetailsOpen} onClose={() => setIsViewDraftDetailsOpen(false)}>
            <h2>Payment Change Draft Details</h2>
            <hr/>
            <h3>Overview</h3>
            <p>These are the transactions previously adjusted</p>
            {/* <button id="btn-calculate-payment-change" className="btn btn-secondary"  onClick={handleClickChangePaymentTransactions} >Calculate</button>
            <button id="btn-save-changes" className="btn btn-secondary" onClick={handleClickSaveDraft} >Save Draft</button><br/><br/> */}
            {isVisible && (<span className="spinner"></span>)}
             <div className="table-container table-sm">
                  {showTable &&resultHistoryDetailsTrans.length > 0 && (
                    <table className="table-bordered scrollable-table">
                      <thead>
                        <tr>
                          <th onClick={() => handleSort('ItemNo')} title="Sort by Item #">Item # {sortColumn === 'ItemNo' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('DebtID')} title="Sort by Debt ID">Debt ID {sortColumn === 'DebtID' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('TransID')} title="Sort by Trans ID ">Trans ID {sortColumn === 'TransID' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('ClearedDate')} title="Sort by Cleared Date">Cleared Date {sortColumn === 'ClearedDate' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('Transaction')} title="Sort by Transaction">Transaction {sortColumn === 'Transaction' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('Amount')} title="Sort by Amount">Amount {sortColumn === 'Amount' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('Status')} title="Sort by Status">Status {sortColumn === 'Status' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('EndingBalance')} title="Sort by Ending Balance">Ending Balance {sortColumn === 'EndingBalance' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('ProposedAmount')} title="Sort by Proposed Amount">Proposed Amount {sortColumn === 'ProposedAmount' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('ProposedNewEndingBalance')} title="Sort by Proposed New Ending Balance">Proposed New Ending Balance {sortColumn === 'ProposedNewEndingBalance' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('FinalizedAmount')} title="Sort by Actual Amount">Finalized Amount {sortColumn === 'FinalizedAmount' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('FinalizedNewEndingBalance')} title="Sort by Finalized New Ending Balance">Finalized New Ending Balance {sortColumn === 'ActualNewEndingBalance' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        </tr>
                      </thead>
                     <tbody>
                      {showTable && resultHistoryDetailsTrans.length > 0 ? (
                        showTable && resultHistoryDetailsTrans.map((row) => (
                          <tr
                            key={row.ItemNo}
                            style={{
                              backgroundColor:
                                row.OrigEndingBalance < 0 ?  '#ffeaeaff' : 'inherit',
                            }}
                            title={"Created By: " + row.CreatedBy + " - Header ID: " + row.HeaderID}
                          >
                            <td>{row.ItemNo}</td>
                            <td>{row.DebtID || 'N/A'}</td>
                            <td>{row.TransID || 'N/A'}</td>
                            <td>{row.ClearedDate || 'N/A'}</td>
                            <td>{row.Transaction || 'N/A'}</td>
                             <td>
                              <span
                               style={{
                                  color: 'green',
                                }}
                              >
                              {formatCurrencyUSD(Number(row.OrigAmount || '0.00').toFixed(2))}
                              </span>
                            </td>
                            <td>
                              <span
                                className="badge"
                                style={{
                                  backgroundColor:
                                    row.OrigStatus?.toUpperCase() === 'COMPLETED'
                                    ? '#b7ffd8ff'
                                    : row.OrigStatus?.toUpperCase() === 'SCHEDULED'
                                    ? '#ffebb7ff'
                                    : 'inherit',
                                }}
                              >
                                {row.OrigStatus || 'N/A'}
                              </span>
                            </td>
                            <td>
                              <span
                                style={{
                                  color: row.OrigEndingBalance < 0 ? 'red' : 'green',
                                }}
                              >
                                {formatCurrencyUSD(
                                  Number(row.OrigEndingBalance || '0.00').toFixed(2)
                                )}
                              </span>
                            </td>

                            <td>
                              <span
                               style={{
                                  color: 'blue',
                                }}
                              >{formatCurrencyUSD(Number(row.ProposedAmount || '0.00').toFixed(2))}
                              </span>
                            </td> 
                            <td>
                              <span
                                style={{
                                  color: row.ProposedNewEndingBalance < 0 ? 'red' : 'green',
                                }}
                              >
                                {formatCurrencyUSD(
                                  Number(row.ProposedNewEndingBalance || '0.00').toFixed(2)
                                )}
                              </span>
                            </td>
                            <td>
                              <span
                               style={{
                                  color: 'blue',
                                }}
                              >{formatCurrencyUSD(Number(row.FinalizedAmount || '0.00').toFixed(2))}
                              </span>
                            </td> 
                            <td>
                              <span
                                style={{
                                  color: row.FinalizedNewEndingBalance < 0 ? 'red' : 'green',
                                }}
                              >
                                {formatCurrencyUSD(
                                  Number(row.FinalizedNewEndingBalance || '0.00').toFixed(2)
                                )}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9">No data available</td>
                        </tr>
                      )}
                    </tbody>

                    </table>
                  )}
            </div>
        </Modal>

          {/* Line Chart for Ending Balance over Cleared Date */}
          {resultTrans.length > 0 && (() => {
            const chartData = prepareChartData();
            return chartData.categories.length > 0 ? (
              <Box sx={{ 
                width: '100%', 
                height: { xs: '300px', sm: '400px', md: '500px', lg: '600px' },
                mx: 'auto',
                p: { xs: 1, sm: 2 },
                overflow: 'hidden' // Prevent overflow
              }}>
                <BalanceLineChart resultTrans={resultTrans} />
              </Box>
            ) : null;
          })()}

          {/* <button className="btn btn-secondary"  onClick={handleClickTransactions} >View Transaction Details</button><br/><br/> */}
          
          <Box sx={{ width: '100%' }}>
            <Tabs value={value} onChange={handleChange} aria-label="basic tabs">
              <Tab label="All Transactions" />
              <Tab label="Client Debits" />
              <Tab label="Change History" />
              {/* <Tab label="Completed" />
              <Tab label="Scheduled" />
              <Tab label="Pending" /> */}
              {/* <Tab label="Tab Three" /> */}
            </Tabs>
            <TabPanel value={value} index={0}>
            
              <DebtIDFilterDropdown
                selectedFilter={selectedDebtIdFilter}
                onChange={setSelectedDebtIdFilter}
                options={uniqueDebtIds}
              />

              <TableNameFilterDropdown
                selectedFilter={selectedTableNameFilter}
                onChange={setSelectedTableNameFilter}
                options={uniqueTableNames}
              />

              <StatusFilterDropdown
                selectedFilter={selectedStatusFilter}
                onChange={setSelectedStatusFilter}
                options={filteredUniqueStatuses}
              />

              <div style={{ display: 'inline-flex', flexDirection: 'column', minWidth: '140px', marginBottom: '10px', marginLeft: '10px' }}>
                <label style={{ fontSize: '1em', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                  [Cleared Date] From 
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  style={{
                    border: '1px solid #D1D5DB',
                    borderRadius: '7px',
                    padding: '10px',
                    fontSize: '1em',
                    backgroundColor: 'white',
                    width: '150px'
                  }}
                />
              </div>

              <div style={{ display: 'inline-flex', flexDirection: 'column', minWidth: '140px', marginBottom: '10px', marginLeft: '10px' }}>
                <label style={{ fontSize: '1em', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                  [Cleared Date] To 
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  style={{
                    border: '1px solid #D1D5DB',
                    borderRadius: '7px',
                    padding: '10px',
                    fontSize: '1em',
                    backgroundColor: 'white',
                    width: '150px'
                  }}
                />
              </div>
                
              <div style={{ display: 'inline-flex', flexDirection: 'column', minWidth: '100px', marginBottom: '10px', marginLeft: '10px' }}>
                <button
                  className="btn"
                  style={{ marginLeft: '10px', height: '40px', alignSelf: 'flex-end', position: 'relative', top: '30px', backgroundColor: '#fc8484ff' }}
                  onClick={clearFilters}
                >
                  Clear Filters
                </button>
              </div>

                <div className="table-container table-sm">
                  {resultTrans.length > 0 && (
                    <table className="table-bordered scrollable-table">
                      <thead>
                        <tr>
                          <th onClick={() => handleSort('CounterID')} title="Sort by Item #">Item # {sortColumn === 'CounterID' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('DebtID')} title="Sort by Debt ID">Debt ID {sortColumn === 'DebtID' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('TransID')} title="Sort by Trans ID ">Trans ID {sortColumn === 'TransID' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('ProcessDate')} title="Sort by Process Date">Process Date {sortColumn === 'ProcessDate' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('ClearedDate')} title="Sort by Cleared Date">Cleared Date {sortColumn === 'ClearedDate' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('Transaction')} title="Sort by Transaction">Transaction {sortColumn === 'Transaction' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('Amount')} title="Sort by Amount">Amount {sortColumn === 'Amount' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('OriginalStatus')} title="Sort by Status">Original Status {sortColumn === 'OriginalStatus' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('OverallStatus')} title="Sort by Status">Overall Status {sortColumn === 'OverallStatus' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('EndingBalance')} title="Sort by Current Ending Balance">Current Ending Balance {sortColumn === 'CurrentEndingBalance' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('EndingBalance')} title="Sort by Forecast Ending Balance">Forecast Ending Balance {sortColumn === 'ForecastEndingBalance' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        </tr>
                      </thead>
                     <tbody>
                      {sortedData.length > 0 ? (
                        sortedData.map((row) => (
                          <tr
                            key={row.CounterID}
                            style={{
                              backgroundColor:
                                row.EndingBalance < 0 ?  '#ffeaeaff' : 'inherit',
                            }}
                          >
                            <td>{row.CounterID}</td>
                            <td>{row.DebtID || 'N/A'}</td>
                            <td>{row.TransID || 'N/A'}</td>
                            <td>{row.ProcessDate || 'N/A'}</td>
                            <td>{row.ClearedDate || 'N/A'}</td>
                            <td>{row.TransactionTitle || 'N/A'}</td>
                            <td>
                              <span
                               style={{
                                  color: 
                                      row.Type?.toUpperCase() === "CREDIT" 
                                      ? 'red' 
                                      : row.Type?.toUpperCase() === "DEBIT"
                                      ? 'green'
                                      : 'purple',
                                }}
                              >
                              {
                                row.Type?.toUpperCase() === "CREDIT"  
                                ? ' - ' 
                                : row.Type?.toUpperCase() === "DEBIT"
                                ? ' + ' 
                                : ''
                              } 
                              {formatCurrencyUSD(Number(row.Amount || '0.00').toFixed(2))}
                              </span>
                            </td>
                            <td>{row.OriginalTransactionStatus || 'N/A'}</td>
                            <td>
                              <span
                                className="badge"
                                style={{
                                  backgroundColor:
                                    row.TransactionStatus?.toUpperCase() === 'COMPLETED'
                                    ? '#b7ffd8ff'
                                    : row.TransactionStatus?.toUpperCase() === 'SCHEDULED'
                                    ? '#ffebb7ff'
                                    : 'inherit',
                                }}
                              >
                                {row.TransactionStatus || 'N/A'}
                              </span>
                            </td>
                            <td>
                              <span
                                style={{
                                  color: row.CurrentEndingBalance < 0 
                                  ? 'red' 
                                  : 'green',
                                }}
                                title={
                                    row.Type?.toUpperCase() === "CREDIT"  && row.TransactionStatus ==='COMPLETED'
                                    ? 'Ending Balance - Decreased'
                                    : row.Type?.toUpperCase() === "CREDIT"  && row.TransactionStatus !=='COMPLETED'
                                    ? 'Ending Balance - No Changes'
                                    : row.Type?.toUpperCase() === "DEBIT" && row.TransactionStatus ==='COMPLETED'
                                    ? 'Ending Balance - Increased'
                                    : row.Type?.toUpperCase() === "DEBIT"  && row.TransactionStatus !=='COMPLETED'
                                    ? 'Ending Balance - No Changes'
                                    : 'Ending Balance - No Changes'
                                  }
                              >
                                {formatCurrencyUSD(
                                  Number(row.CurrentEndingBalance || '0.00').toFixed(2)
                                )}{' '}
                                <span
                                  style={{
                                    color: 
                                    row.Type?.toUpperCase() === "CREDIT"  
                                    ? 'red' 
                                    : row.Type?.toUpperCase() === "DEBIT"
                                    ? 'green' 
                                    : 'purple',
                                  }}
                                >
                                  {
                                  row.Type?.toUpperCase() === "CREDIT"  && row.TransactionStatus ==='COMPLETED'
                                  ? '⬇︎' 
                                  : row.Type?.toUpperCase() === "CREDIT"  && row.TransactionStatus !=='COMPLETED'
                                  ? '➖' 
                                  : row.Type?.toUpperCase() === "DEBIT" && row.TransactionStatus ==='COMPLETED'
                                  ? '⬆︎' 
                                  : row.Type?.toUpperCase() === "DEBIT"  && row.TransactionStatus !=='COMPLETED'
                                  ? '➖' 
                                  : '➖'
                                  } 
                                </span>
                              </span>
                            </td>
                            <td>
                              <span
                                style={{
                                  color: row.ForecastEndingBalance < 0 
                                  ? 'red' 
                                  : 'green',
                                }}
                                title={
                                    row.Type?.toUpperCase() === "CREDIT"  
                                    ? 'Ending Balance - Decreased'
                                    : row.Type?.toUpperCase() === "DEBIT"
                                    ? 'Ending Balance - Increased'
                                    : 'Ending Balance - No Changes'
                                  }
                              >
                                {formatCurrencyUSD(
                                  Number(row.ForecastEndingBalance || '0.00').toFixed(2)
                                )}{' '}
                                <span
                                  style={{
                                    color: 
                                    row.Type?.toUpperCase() === "CREDIT"  
                                    ? 'red' 
                                    : row.Type?.toUpperCase() === "DEBIT"
                                    ? 'green' 
                                    : 'purple',
                                  }}
                                >
                                  {
                                  row.Type?.toUpperCase() === "CREDIT"
                                  ? '⬇︎' 
                                  : row.Type?.toUpperCase() === "DEBIT" 
                                  ? '⬆︎' 
                                  : '➖'
                                  } 
                                </span>
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="11">No data available</td>
                        </tr>
                      )}
                    </tbody>

                    </table>
                  )}
                </div>
            </TabPanel>
            <TabPanel value={value} index={1}>
                <div className="table-container table-sm">
                  {resultTrans.length > 0 && (
                    <table className="table-bordered scrollable-table">
                      <thead>
                        <tr>
                          <th onClick={() => handleSort('CounterID')} title="Sort by Item #">Item # {sortColumn === 'CounterID' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('DebtID')} title="Sort by Debt ID">Debt ID {sortColumn === 'DebtID' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('TransID')} title="Sort by Trans ID ">Trans ID {sortColumn === 'TransID' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('ProcessDate')} title="Sort by Process Date">Process Date {sortColumn === 'ProcessDate' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('ClearedDate')} title="Sort by Cleared Date">Cleared Date {sortColumn === 'ClearedDate' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('Transaction')} title="Sort by Transaction">Transaction {sortColumn === 'Transaction' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('Amount')} title="Sort by Amount">Amount {sortColumn === 'Amount' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('OriginalStatus')} title="Sort by Status">Original Status {sortColumn === 'OriginalStatus' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('OverallStatus')} title="Sort by Status">Overall Status {sortColumn === 'OverallStatus' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('EndingBalance')} title="Sort by Current Ending Balance">Current Ending Balance {sortColumn === 'CurrentEndingBalance' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('EndingBalance')} title="Sort by Forecast Ending Balance">Forecast Ending Balance {sortColumn === 'ForecastEndingBalance' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resultTrans.length > 0 ? (
                          resultTrans
                            .filter((row) => row.TransactionTitle === "ACH Client Debit")
                            .map((row) => (
                              <tr key={row.CounterID}
                                style={{
                                      backgroundColor:
                                        row.EndingBalance < 0
                                          ? '#ffeaeaff'      
                                          : 'inherit',
                                    }}
                              >
                                <td>{row.CounterID}</td>
                                <td>{row.DebtID || 'N/A'}</td>
                                <td>{row.TransID || 'N/A'}</td>
                                <td>{row.ProcessDate || 'N/A'}</td>
                                <td>{row.ClearedDate || 'N/A'}</td>
                                <td>{row.TransactionTitle || 'N/A'}</td>
                                <td>
                                  <span
                                  style={{
                                      color: 
                                          row.Type?.toUpperCase() === "CREDIT" 
                                          ? 'red' 
                                          : row.Type?.toUpperCase() === "DEBIT"
                                          ? 'green'
                                          : 'purple',
                                    }}
                                  >
                                  {
                                    row.Type?.toUpperCase() === "CREDIT"  
                                    ? ' - ' 
                                    : row.Type?.toUpperCase() === "DEBIT"
                                    ? ' + ' 
                                    : ''
                                  } 
                                  {formatCurrencyUSD(Number(row.Amount || '0.00').toFixed(2))}
                                  </span>
                                </td>
                                <td>{row.OriginalTransactionStatus || 'N/A'}</td>
                                <td>
                                  <span
                                    className="badge"
                                    style={{
                                      backgroundColor:
                                        row.TransactionStatus?.toUpperCase() === 'COMPLETED'
                                        ? '#b7ffd8ff'
                                        : row.TransactionStatus?.toUpperCase() === 'SCHEDULED'
                                        ? '#ffebb7ff'
                                        : 'inherit',
                                    }}
                                  >
                                    {row.TransactionStatus || 'N/A'}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    style={{
                                      color: row.CurrentEndingBalance < 0 
                                      ? 'red' 
                                      : 'green',
                                    }}
                                    title={
                                        row.Type?.toUpperCase() === "CREDIT"  && row.TransactionStatus ==='COMPLETED'
                                        ? 'Ending Balance - Decreased'
                                        : row.Type?.toUpperCase() === "CREDIT"  && row.TransactionStatus !=='COMPLETED'
                                        ? 'Ending Balance - No Changes'
                                        : row.Type?.toUpperCase() === "DEBIT" && row.TransactionStatus ==='COMPLETED'
                                        ? 'Ending Balance - Increased'
                                        : row.Type?.toUpperCase() === "DEBIT"  && row.TransactionStatus !=='COMPLETED'
                                        ? 'Ending Balance - No Changes'
                                        : 'Ending Balance - No Changes'
                                      }
                                  >
                                    {formatCurrencyUSD(
                                      Number(row.CurrentEndingBalance || '0.00').toFixed(2)
                                    )}{' '}
                                    <span
                                      style={{
                                        color: 
                                        row.Type?.toUpperCase() === "CREDIT"  
                                        ? 'red' 
                                        : row.Type?.toUpperCase() === "DEBIT"
                                        ? 'green' 
                                        : 'purple',
                                      }}
                                    >
                                      {
                                      row.Type?.toUpperCase() === "CREDIT"  && row.TransactionStatus ==='COMPLETED'
                                      ? '⬇︎' 
                                      : row.Type?.toUpperCase() === "CREDIT"  && row.TransactionStatus !=='COMPLETED'
                                      ? '➖' 
                                      : row.Type?.toUpperCase() === "DEBIT" && row.TransactionStatus ==='COMPLETED'
                                      ? '⬆︎' 
                                      : row.Type?.toUpperCase() === "DEBIT"  && row.TransactionStatus !=='COMPLETED'
                                      ? '➖' 
                                      : '➖'
                                      } 
                                    </span>
                                  </span>
                                </td>
                                <td>
                                  <span
                                    style={{
                                      color: row.ForecastEndingBalance < 0 
                                      ? 'red' 
                                      : 'green',
                                    }}
                                    title={
                                        row.Type?.toUpperCase() === "CREDIT"  
                                        ? 'Ending Balance - Decreased'
                                        : row.Type?.toUpperCase() === "DEBIT"
                                        ? 'Ending Balance - Increased'
                                        : 'Ending Balance - No Changes'
                                      }
                                  >
                                    {formatCurrencyUSD(
                                      Number(row.ForecastEndingBalance || '0.00').toFixed(2)
                                    )}{' '}
                                    <span
                                      style={{
                                        color: 
                                        row.Type?.toUpperCase() === "CREDIT"  
                                        ? 'red' 
                                        : row.Type?.toUpperCase() === "DEBIT"
                                        ? 'green' 
                                        : 'purple',
                                      }}
                                    >
                                      {
                                      row.Type?.toUpperCase() === "CREDIT"
                                      ? '⬇︎' 
                                      : row.Type?.toUpperCase() === "DEBIT" 
                                      ? '⬆︎' 
                                      : '➖'
                                      } 
                                    </span>
                                  </span>
                                </td>
                              </tr>
                            ))
                        ) : (
                          <tr>
                            <td colSpan="9">No data available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
            </TabPanel>
            <TabPanel value={value} index={2}>
              <div className="table-container table-sm">
                {resultHistoryHeaderTrans.length > 0 ? (
                  <table className="table-bordered scrollable-table">
                    <thead>
                      <tr>
                        <th onClick={() => handleSortChangeHistory('CounterID')} title="Sort by Item #">
                          Item # {sortColumn === 'CounterID' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th onClick={() => handleSortChangeHistory('HeaderID')} title="Sort by Header ID">
                          Header ID {sortColumn === 'HeaderID' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th onClick={() => handleSortChangeHistory('TotalCount')} title="Sort by Total Count">
                          Total Count {sortColumn === 'TotalCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th onClick={() => handleSortChangeHistory('CreatedBy')} title="Sort by Created By">
                          Created By {sortColumn === 'CreatedBy' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th onClick={() => handleSortChangeHistory('CreatedDate')} title="Sort by Created Date">
                          Created Date {sortColumn === 'CreatedDate' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultHistoryHeaderTrans
                        .map(row => (
                          <tr
                            key={row.CounterID}
                          >
                            <td>{row.CounterID}</td>
                            <td>{row.HeaderID || 'N/A'}</td>
                            <td>{row.TotalCount || 0}</td>
                            <td>{row.CreatedBy || 'N/A'}</td>
                            <td>{row.CreatedDate || 'N/A'}</td>
                            <td>
                              <button 
                                className="btn btn-primary btn-sm"
                                onClick={() => handleViewDetails(row.HeaderID)}
                                >
                                  View
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                ) : (
                  <p>No data available</p>
                )}
              </div>
            </TabPanel>   
           
          </Box>
        </div>
      </div>
      {/* <div style={{ padding: '20px' }}>
        <h1>React Modal Example</h1>
        <button onClick={() => setIsModalOpen(true)}>Open Modal</button>

        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <h2>Modal Title</h2>
          <p>This is the content inside the modal!</p>
        </Modal>
      </div> */}
    </div>
  );
};

export default TransactionSummary;
